import {
  differenceInDays,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  max,
  min,
  isValid,
} from 'date-fns';
import type { IconType } from '@cognite/cogs.js';
import {
  MilestoneType,
  Milestone,
  Ruleset,
  Rule,
} from 'types/dt-graphql-apollo-v2';
import { sortAlphabeticallyByLabel } from 'utils/data-validation';
import { convertISODateToJSDate } from 'utils/misc';
import { DefaultOptionType } from 'antd/lib/cascader';
import { Property, Schema } from 'hooks/projects';
import { Maybe } from 'types/graphql-apollo';
import {
  isConditionWithValueLabel,
  LogicalCondition,
  LogicalRule,
  RelationalCondition,
  RelationalRule,
} from './rules';
import { getSchemaNameForProperty } from './schemas';

export const term = {
  Milestone: 'Milestone',
  Milestones: 'Milestones',
  MilestoneCollection: 'Milestone collection',
  MilestoneCollections: 'Milestone collections',
  OpenCollection: 'Open collection',
  OpenCollections: 'Open collections',
  Collection: 'Collection',
  Collections: 'Collections',
  Ruleset: 'Ruleset',
  Rulesets: 'Rulesets',
  Rule: 'Rule',
  Rules: 'Rules',
  SchemaType: 'Schema type',
  milestone: 'milestone',
  milestones: 'milestones',
  milestoneCollection: 'milestone collection',
  milestoneCollections: 'milestone collections',
  openCollection: 'open collection',
  openCollections: 'open collections',
  collection: 'collection',
  collections: 'collections',
  ruleset: 'ruleset',
  rulesets: 'rulesets',
  rule: 'rule',
  rules: 'rules',
  schemaType: 'schema type',
};

export const filterRulesetOrRuleBySearch = (
  searchValue: string,
  dataToBefiltered: (Rule | Ruleset)[]
) => {
  if (!searchValue || !dataToBefiltered) return dataToBefiltered;
  const searchTerm = searchValue.trim().toLowerCase();
  return dataToBefiltered?.filter((data: Rule | Ruleset) => {
    const searchTargets = [data?.name];
    return searchTargets.some(
      (searchTarget) =>
        searchTarget && searchTarget?.toLowerCase().includes(searchTerm)
    );
  });
};

export const getConditionLabel = (condition: RelationalCondition) => {
  switch (condition) {
    case RelationalCondition.EQUALS:
      return 'Equals (==)';
    case RelationalCondition.NOT_EQUALS:
      return 'Not Equals (!=)';
    case RelationalCondition.GREATER_THAN:
      return 'Greater than (>)';
    case RelationalCondition.GREATER_THAN_EQUALS:
      return 'Greater than or equals (≥)';
    case RelationalCondition.LESS_THAN:
      return 'Less than (<)';
    case RelationalCondition.LESS_THAN_EQUALS:
      return 'Less than or equals (≤)';
    case RelationalCondition.EXISTS:
      return 'Exists (Not null)';
    case RelationalCondition.IS_UNIQUE:
      return 'Is unique';
    case RelationalCondition.NOT_EXISTS:
      return 'Does not exist (Null)';
    case RelationalCondition.IS_ONE_OF:
      return 'Is one of (In set […])';
    case RelationalCondition.IS_NOT_ONE_OF:
      return 'Is not one of (Not in set […])';
    case RelationalCondition.STARTS_WITH:
      return 'Starts with (f.ex: A)';
    case RelationalCondition.NOT_STARTS_WITH:
      return 'Does not start with (f.ex: A)';
    case RelationalCondition.ENDS_WITH:
      return 'Ends with (f.ex: B)';
    case RelationalCondition.NOT_ENDS_WITH:
      return 'Does not end with (f.ex: B)';
    case RelationalCondition.REGEX:
      return 'Matches RegEx pattern';
    case RelationalCondition.NOT_REGEX:
      return 'Does not match RegEx pattern';
    default:
      return 'Select condition…';
  }
};

/* Get schema types options from current schemas */
export const getSchemaTypeOptions = (schemas: Schema[] = []) => {
  const schemaTypes = schemas.map((schema) => ({
    label: schema.name,
    value: schema.name,
  }));

  return sortAlphabeticallyByLabel(schemaTypes) as typeof schemaTypes;
};

interface ConditionOptionsParams {
  isPropertyComparison: boolean;
  isPureRelation: boolean;
  enabledEndsWith?: boolean;
  enabledIsUnique?: boolean;
  dataType?: string;
  schema?: Schema;
}

// Check if a property is an actual schema type
const typeIsRootRelation = (fieldType: string, schema?: Schema) => {
  const prop = schema?.properties.find((p) => p.type === fieldType);

  // Found the property with the type fieldType and that is a schema type as well
  return !!prop?.schema;
};

export const getConditionOptions = ({
  isPropertyComparison,
  isPureRelation,
  enabledEndsWith = false,
  enabledIsUnique = false,
  dataType = '',
  schema,
}: ConditionOptionsParams) => {
  const isRootRelation = typeIsRootRelation(dataType, schema);

  dataType = dataType.replaceAll('!', '').replace(/^\[/, '').replace(/\]$/, '');
  const enabledRegex = dataType === 'String';

  if (isPureRelation || isRootRelation) {
    return [...existsConditionOptions];
  }
  const conditions = [...commonConditionOptions];

  if (!isPropertyComparison) {
    conditions.push(...oneOfConditionOptions);
  }

  if (enabledRegex) {
    conditions.push(...regexConditionOptions);
  }

  if (dataType === '') {
    conditions.push(
      ...[...startsWithConditionOptions, ...numberConditionOptions]
    );
  }

  if (enabledEndsWith && (dataType === '' || dataType === 'String')) {
    conditions.push(...endsWithConditionOptions);
  }

  if (dataType === 'String') {
    conditions.push(...startsWithConditionOptions);
  }

  if (dataType === 'Int' || dataType === 'Float') {
    conditions.push(...numberConditionOptions);
  }

  if (enabledIsUnique) {
    conditions.push(...isUniqueConditionOptions);
  }
  return conditions;
};

const existsConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.EXISTS),
    value: RelationalCondition.EXISTS,
  },
  {
    label: getConditionLabel(RelationalCondition.NOT_EXISTS),
    value: RelationalCondition.NOT_EXISTS,
  },
];

const commonConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.EQUALS),
    value: RelationalCondition.EQUALS,
  },
  {
    label: getConditionLabel(RelationalCondition.NOT_EQUALS),
    value: RelationalCondition.NOT_EQUALS,
  },
  ...existsConditionOptions,
];

const oneOfConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.IS_ONE_OF),
    value: RelationalCondition.IS_ONE_OF,
  },
  {
    label: getConditionLabel(RelationalCondition.IS_NOT_ONE_OF),
    value: RelationalCondition.IS_NOT_ONE_OF,
  },
];

const numberConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.GREATER_THAN),
    value: RelationalCondition.GREATER_THAN,
  },
  {
    label: getConditionLabel(RelationalCondition.GREATER_THAN_EQUALS),
    value: RelationalCondition.GREATER_THAN_EQUALS,
  },
  {
    label: getConditionLabel(RelationalCondition.LESS_THAN),
    value: RelationalCondition.LESS_THAN,
  },
  {
    label: getConditionLabel(RelationalCondition.LESS_THAN_EQUALS),
    value: RelationalCondition.LESS_THAN_EQUALS,
  },
];

const endsWithConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.ENDS_WITH),
    value: RelationalCondition.ENDS_WITH,
  },
  {
    label: getConditionLabel(RelationalCondition.NOT_ENDS_WITH),
    value: RelationalCondition.NOT_ENDS_WITH,
  },
];
const startsWithConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.STARTS_WITH),
    value: RelationalCondition.STARTS_WITH,
  },
  {
    label: getConditionLabel(RelationalCondition.NOT_STARTS_WITH),
    value: RelationalCondition.NOT_STARTS_WITH,
  },
];

const regexConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.REGEX),
    value: RelationalCondition.REGEX,
  },
  {
    label: getConditionLabel(RelationalCondition.NOT_REGEX),
    value: RelationalCondition.NOT_REGEX,
  },
];

const isUniqueConditionOptions = [
  {
    label: getConditionLabel(RelationalCondition.IS_UNIQUE),
    value: RelationalCondition.IS_UNIQUE,
  },
];

const getLogicalOperatorLabel = (logicalOperator: LogicalCondition) => {
  switch (logicalOperator) {
    case LogicalCondition.AND:
      return 'and';
    case LogicalCondition.OR:
      return 'or';
    default:
      return 'and';
  }
};

const logicalOperatorsOptions = [
  {
    label: getLogicalOperatorLabel(LogicalCondition.AND),
    value: LogicalCondition.AND,
  },
  {
    label: getLogicalOperatorLabel(LogicalCondition.OR),
    value: LogicalCondition.OR,
  },
];
/* Milestones */
export enum MilestoneStatus {
  ACTIVE = 'Active',
  ARCHIVED = 'Archived',
}

export const formatMilestoneDateToString = (
  date: Date | null
): string | null | undefined => {
  if (!date || !isValid(date)) return null;
  return format(date, 'yyyy-MM-dd');
};

export const getAllOpenCollections = (
  milestones: Milestone[] | null | undefined
) => milestones?.filter((m) => m.milestoneType === MilestoneType.Static);

export const getAllMilestoneCollections = (
  milestones: Milestone[] | null | undefined
) => milestones?.filter((m) => m.milestoneType === MilestoneType.Default);

export const getNextMilestoneOrderIndex = (
  milestones: Milestone[] | null | undefined
) => {
  const highestOrderIndex = milestones?.reduce(
    (orderIndex = 0, currentMilestone) => {
      if (!currentMilestone?.orderIndex) return orderIndex;
      if (currentMilestone.orderIndex > orderIndex)
        return currentMilestone.orderIndex;
      return orderIndex;
    },
    0
  );
  return highestOrderIndex !== undefined ? highestOrderIndex + 1 : 0;
};

export const getEarliestDateFromMilestones = (
  milestones: Milestone[] | null | undefined
): string | null | undefined => {
  if (!milestones?.length) return undefined;

  // Is only relevant to milestones with MilestoneType.DEFAULT
  const filteredMilestones = getAllMilestoneCollections(milestones);
  if (!filteredMilestones?.length) return undefined;

  const allStartActiveTime = filteredMilestones?.reduce((acc: Date[], cur) => {
    if (!cur?.startActiveTime) return acc;
    const date = convertISODateToJSDate(cur?.startActiveTime);
    if (!date) return acc;
    return [...acc, date];
  }, []);
  const earliestActiveTime = min(allStartActiveTime);
  return formatMilestoneDateToString(earliestActiveTime);
};

export const getLatestDateFromMilestones = (
  milestones: Milestone[] | null
): string | null | undefined => {
  if (!milestones?.length) return undefined;

  // Is only relevant to milestones with MilestoneType.DEFAULT
  const filteredMilestones = getAllMilestoneCollections(milestones);
  if (!filteredMilestones?.length) return undefined;

  const allEndActiveTime = filteredMilestones?.reduce((acc: Date[], cur) => {
    if (!cur?.endActiveTime) return acc;
    const date = convertISODateToJSDate(cur?.endActiveTime);
    if (!date) return acc;
    return [...acc, date];
  }, []);
  const latestActiveTime = max(allEndActiveTime);
  return formatMilestoneDateToString(latestActiveTime);
};

export const getPreviousMilestoneEndActiveTime = (
  milestone: Milestone | null | undefined,
  allMilestones: Milestone[] | null | undefined
) => {
  if (
    !allMilestones ||
    (milestone && milestone?.milestoneType !== MilestoneType.Default)
  )
    return null;

  // Previous milestone is only relevant to all milestones with MilestoneType.DEFAULT
  const filteredMilestones = getAllMilestoneCollections(allMilestones);
  if (!filteredMilestones?.length) return null;

  // index of the milestone or the length of all filteredMilestones to get the latest endActiveTime
  const indexOfMilestone = milestone
    ? filteredMilestones?.indexOf(milestone)
    : filteredMilestones.length;

  const previousMilestone = indexOfMilestone
    ? filteredMilestones[indexOfMilestone - 1]
    : null;

  const previousEndActiveTime =
    convertISODateToJSDate(previousMilestone?.endActiveTime) || null;

  return previousEndActiveTime;
};

export const getMilestoneActiveTime = (
  milestone: Milestone,
  allMilestones: Milestone[] | undefined
): { start: Date | null; end: Date | null; now: Date } => {
  const previousEndActiveTime = getPreviousMilestoneEndActiveTime(
    milestone,
    allMilestones
  );

  const startActiveTime =
    convertISODateToJSDate(milestone?.startActiveTime) || null;

  const start = previousEndActiveTime || startActiveTime;

  const end = convertISODateToJSDate(milestone?.endActiveTime) || null;

  const now = new Date();

  return { start, end, now };
};

export const getMilestoneStatus = (
  allMilestones: Milestone[] | [],
  milestone?: Milestone
): MilestoneStatus | null => {
  if (!milestone) return null;
  const { start, end, now } = getMilestoneActiveTime(milestone, allMilestones);

  if (!start || !end) return null;

  if (isAfter(now, start) && isBefore(now, end)) {
    return MilestoneStatus.ACTIVE;
  }

  if (isAfter(now, end)) {
    return MilestoneStatus.ARCHIVED;
  }

  return null;
};

export const getMilestoneProgress = (
  allMilestones: Milestone[] | [],
  milestone?: Milestone
): number | null => {
  if (!milestone) return null;

  // Keep only the milestones from the same schema collection
  const filteredMilestones = allMilestones.filter(
    (m) =>
      m.schemaCollection?.externalId === milestone.schemaCollection?.externalId
  );
  // Sort the milestone based on their orderIndex value
  const sortedMilestones = filteredMilestones.sort(
    (m1, m2) => (m1?.orderIndex || 0) - (m2?.orderIndex || 0)
  );

  const { start, end, now } = getMilestoneActiveTime(
    milestone,
    sortedMilestones
  );

  if (!start || !end) return null;

  if (isAfter(now, start) && isBefore(now, end)) {
    const differenceStartNow = differenceInMinutes(start, now);
    const differenceStartEnd = differenceInMinutes(start, end);
    const percentage = Math.round(
      (differenceStartNow / differenceStartEnd) * 100
    );
    return percentage;
  }

  if (isAfter(now, end)) {
    return 100;
  }

  return 0;
};

export const getMilestoneActiveTimeDaysUntilEnd = (
  allMilestones: Milestone[] | [],
  milestone?: Milestone
): number | null => {
  if (!milestone) return null;
  const { start, end, now } = getMilestoneActiveTime(milestone, allMilestones);

  if (!start || !end) return null;

  if (isAfter(now, start) && isBefore(now, end)) {
    const differenceNowEnd = differenceInDays(end, now);
    return differenceNowEnd;
  }

  if (isBefore(now, start)) {
    return null;
  }

  return 0;
};

export const getMilestoneIconType = (
  milestone: Milestone | null,
  allMilestones: Milestone[] | []
): IconType | null => {
  if (!milestone) return null;
  const { start, end, now } = getMilestoneActiveTime(milestone, allMilestones);

  if (!start || !end) return null;

  if (isAfter(now, start) && isBefore(now, end)) {
    return 'CheckmarkAlternative';
  }

  if (isAfter(now, end)) {
    return 'CheckmarkFilled';
  }

  return 'Calendar';
};

/** Returns rules that are related to the provided ruleset id. */
export const getRulesetRules = (
  rulesetId: string,
  rules?: Rule[] | Maybe<Rule>[]
) =>
  (rules?.filter((r) => r?.rulesets?.items[0]?.externalId === rulesetId) ??
    []) as Rule[];

/** Converts a string dat as yyyy-mm-dd into an ISO date. */
export const convertMilestoneDateToISODate = (date: string) =>
  new Date(date).toISOString();

/** Converts an ISO date into a string date as yyyy-mm-dd. */
export const convertISODateToMilestoneDate = (dateISO: string) =>
  dateISO.substring(0, 10);

/* Time and Days >-< cronExpression
 *
 * cronExpressionFormat: 'mm hh * * d' or 'mm hh * * d,d,d,d,d'
 * i.e convert cronExpression to Days and Time
 * cronExpression input: '15 11 * * 1,5';
 * converted output: {days: [1,5] time: '11:15'}
 *
 */
enum Day {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export const getDaysOptions = (selectedDays?: number[]) => {
  const daysOptions: { label: string; value: number }[] = [];
  Object.entries(Day).forEach((enumEntry) => {
    if (parseInt(enumEntry[0], 10) >= 0) {
      const enumNumber = parseInt(enumEntry[0], 10);
      if (
        !selectedDays ||
        (selectedDays && selectedDays.includes(enumNumber))
      ) {
        daysOptions.push({ label: Day[enumNumber], value: enumNumber });
      }
    }
  });
  return daysOptions;
};

const convertCronToTime = (cronExpression?: string) => {
  if (!cronExpression) return null;

  const splitCron = cronExpression.split(' ');
  if (splitCron.length !== 5) return null; // invalid cron
  // hh:mm
  const time = `${splitCron[1]}:${splitCron[0]}`;
  return time;
};

const convertCronToDays = (cronExpression?: string) => {
  if (!cronExpression) return null;

  const splitCron = cronExpression.split(' ');
  if (splitCron.length !== 5) return null; // invalid cron
  // [0,1]
  const days = splitCron[4].split(',').map((number) => Number(number));
  return days;
};

export const convertCronExpressionToTimeAndDays = (
  cronExpression?: string | null
) => {
  if (!cronExpression) return null;
  const time = convertCronToTime(cronExpression);
  const days = convertCronToDays(cronExpression);

  return { time, days };
};

const sortCascaderOptionTypes = (
  a: DefaultOptionType,
  b: DefaultOptionType
) => {
  const aLabel = a?.label?.toString();
  const bLabel = b?.label?.toString();
  if (!bLabel && !aLabel) return 0;
  if (!bLabel) return -1;
  if (!aLabel) return 1;
  return aLabel.localeCompare(bLabel);
};

/** Checks if a property type is an array property.
 * e.g. [String], [Planet] */
export const isPropertyArray = (propertyType: string) =>
  propertyType.startsWith('[') && propertyType.endsWith(']');

/** Checks if a property represents a many-to-many relation with other types */
export const isManyToMany = (property: Property, rootSchema?: Schema) =>
  isPropertyArray(property.type) && !!rootSchema;

const mapPropertyToAttributes = (
  property: Property,
  nestedLevel: number = 0,
  manyToManyLevel: number = 0,
  schemas: Schema[],
  disableManyToMany?: boolean
): DefaultOptionType => {
  const maxNestedLevel = 3; // to end endless loop if relations repeat themselves
  let currentManyToManyLevel = manyToManyLevel;
  const disabledOption = nestedLevel >= maxNestedLevel;
  const scalarPropertiesArray = getSchemaNameForProperty(property, schemas);

  let children: DefaultOptionType['children'];

  const isMany2Many = isManyToMany(property, scalarPropertiesArray);

  // Keep track of the nesting level of ManytoMany
  if (isMany2Many) currentManyToManyLevel += 1;

  // Disable nested ManytoMany
  let disabledManyToMany = currentManyToManyLevel > 1 ? isMany2Many : false;

  // Disable property comparison if we have ManytoMany
  if (disableManyToMany && isMany2Many) {
    disabledManyToMany = disableManyToMany;
  }

  if (scalarPropertiesArray && nestedLevel < maxNestedLevel) {
    children = scalarPropertiesArray?.properties?.map((p) =>
      getSchemaNameForProperty(p, schemas)
        ? mapPropertyToAttributes(
            p,
            nestedLevel + 1,
            currentManyToManyLevel,
            schemas,
            disableManyToMany
          )
        : {
            label: p.name,
            value: p.name,
            type: p.type,
            disabled: disabledManyToMany,
          }
    );
  }

  return {
    label: property.name,
    value: property.name,
    type: property.type,
    disabled: disabledOption || disabledManyToMany,
    isManyToMany: isMany2Many,
    nestedLevel,
    children: children?.sort(sortCascaderOptionTypes),
  };
};

export const mapPropertiesToAttributes = (
  selectedSchema: Schema,
  schemas: Schema[],
  disableManyToMany: boolean = false
) =>
  selectedSchema?.properties
    ?.map((prop) =>
      mapPropertyToAttributes(prop, 0, 0, schemas, disableManyToMany)
    )
    .sort(sortCascaderOptionTypes);

// This function will create conditions where you wouldn't have
// - conditions with no children
// - conditions with on one child
// - conditions with child having same logical condition as parent

export function constructCleanConditions(
  inputRule: LogicalRule | RelationalRule
) {
  // Deep clonning
  const condition = JSON.parse(JSON.stringify(inputRule, undefined, 2)) as
    | LogicalRule
    | RelationalRule;
  // You don't need cleaning of RelationalRules
  if (condition._type === 'RelationalRule') return condition;
  // Getting cleaned up children for Logical Rules
  let children = condition.children
    .map(constructCleanConditions)
    // filter would never let undefined child pass but linter has issues so we need typecasting here
    .filter(Boolean) as Array<LogicalRule | RelationalRule>;
  // If no clean children returned we will also return nothing.
  if (!children || children?.length === 0) return undefined;
  // If there's only one child replace current rule with its own child
  if (children.length === 1) return condition.children[0];
  // when nested logical rules are same are parent
  const flattenedChild: Array<LogicalRule | RelationalRule> = [];
  // Logical children with same conditions can be moved to it's parent.
  children = children.filter((child) => {
    if (
      child._type === 'LogicalRule' &&
      child.condition === condition.condition
    ) {
      flattenedChild.push(...child.children);
      return false;
    }
    return true;
  });
  children.push(...flattenedChild);
  condition.children = children;
  return condition;
}

function getNestedCondition(root: LogicalRule, ancestors: number[]) {
  let itr = root;
  ancestors.forEach((ancestor) => {
    if (itr._type !== 'LogicalRule') throw new Error('Invalid ancestors');
    itr = itr.children[ancestor] as LogicalRule;
  });
  return itr;
}

export function updateCondition(
  condition: LogicalRule,
  ancestors: number[],
  newVal: LogicalRule | RelationalRule
) {
  const cloned = JSON.parse(JSON.stringify(condition)) as LogicalRule;
  const existingVal = getNestedCondition(cloned, ancestors);
  Object.assign(existingVal, newVal);
  return cloned;
}

export function deleteCondition(
  condition: LogicalRule,
  ancestors: number[],
  index: number
) {
  const cloned = JSON.parse(JSON.stringify(condition)) as LogicalRule;
  const existingVal = getNestedCondition(cloned, ancestors);
  existingVal.children?.splice(index, 1);
  const cleanedRules = constructCleanConditions(cloned);
  if (cleanedRules?._type !== 'LogicalRule') {
    const children =
      cleanedRules?._type === 'RelationalRule' ? [cleanedRules] : [];
    return {
      _type: cloned._type,
      condition: cloned.condition,
      children,
    };
  }
  return cleanedRules;
}

export function addNestedCondition(
  condition: LogicalRule,
  ancestors: number[],
  index: number,
  newLogicalCondition: LogicalCondition
) {
  const cloned = JSON.parse(JSON.stringify(condition)) as LogicalRule;
  const existingVal = getNestedCondition(cloned, ancestors);
  // When we want to nest, we need to move 2 children further down a level.
  // We first remove 2 children. Then we create a logical rule and add those 2 as its children.
  const removedChild = existingVal.children?.splice(index, 2);
  const newLogicalRule: LogicalRule = {
    _type: 'LogicalRule',
    condition: newLogicalCondition,
    children: [...removedChild],
  };
  existingVal.children?.push(newLogicalRule);
  return cloned;
}

/** Checks if a condition is valid. If the condition requires a value, then that value needs to be valid as well. */
function isConditionValid(condition: RelationalRule) {
  if (isConditionWithValueLabel(condition.condition)) {
    return !!condition.property && !!condition.value;
  }
  return !!condition.property;
}

export function getConditionsCount(condition: LogicalRule | RelationalRule): {
  total: number;
  valid: number;
} {
  if (condition._type === 'RelationalRule') {
    const valid = isConditionValid(condition) ? 1 : 0;
    return {
      total: 1,
      valid,
    };
  }
  const counts = {
    total: 0,
    valid: 0,
  };
  condition?.children
    ?.map((child) => getConditionsCount(child))
    .forEach((child) => {
      counts.total += child.total;
      counts.valid += child.valid;
    });
  return counts;
}

const maxConditionLogicalDepth = 2;

export function getLogicalOperatorsOptions(
  condition: LogicalRule,
  depth: number
): typeof logicalOperatorsOptions {
  return depth === maxConditionLogicalDepth
    ? logicalOperatorsOptions.filter((l) => l.value === condition.condition)
    : logicalOperatorsOptions;
}
